import Vue from "vue"
import Vuex from "vuex"
import VuexPersistence from "vuex-persist"
import localForage from "localforage"
import _ from "lodash"
import moment from "moment"
import auth from "@/adapter/auth"
import recorder from "@/adapter/recorder"
import orgMng from "@/adapter/organization-manager"
import discussion from "@/adapter/discussion"
import report from "@/adapter/report"
import ailib, { isNotFoundError } from "@/adapter/ailib"
import * as utils from "@/utils"
import { findLongestHighFrame } from "@/utils/discussion"
import { DEFAULT_RUBRIC_ID } from "@/utils/skillRubric"
import conf from "@/config"
import i18n from "@/plugins/i18n"
import { Application } from "@hylable/common"
import { metering } from "@/plugins/hylable-sdk"
import knowledgebase from "../adapter/knowledgebase"

Vue.use(Vuex)

function newStore(storage, pick, needSerialize){
  const serialize = needSerialize ? JSON.stringify : (val) => val
  const deserialize = needSerialize ? JSON.parse : (val) => val

  return new VuexPersistence({
    storage: storage,
    asyncStorage: true,
    saveState: (key, state, storage) => storage.setItem(key, serialize(state)),
    restoreState: async (key, storage) => deserialize(await storage.getItem(key)),
    reducer: (state) => _.pick(state, pick),
  })
}
const localStore = newStore(localForage, ["ui", "favorites"])
const sessionStore = newStore(sessionStorage, ["organization", "course", "recorder", "skill"], true)

const handler = (store) => {
  const prev = {}

  store.subscribe(async (mutation, state) => {
    if(mutation.payload && (
      mutation.type == "oid"
      || mutation.type == "cid"
      || mutation.type == "did"
    )){
      console.debug("start waiting auth", mutation.type)
      for(let counter = 0; counter < 50; counter++) {
        console.debug({counter})
        if (store.state.auth.loginUser) break
        await utils.sleep(100)
      }
      console.debug("finish waiting auth")
    }

    if(mutation.type == "auth/loginUser"){
      const user = mutation.payload
      const info = auth.getLogininfo(user)
      store.commit("auth/loginInfo", info)
      store.commit("oid", info.oid)
      store.commit("type", info.type)

      if(!user){
        store.commit("organization/clear")
        store.commit("skill/clear")
        store.commit("course/list", [])
        store.commit("member/list", [])
        store.commit("recorder/list", [])
        store.commit("discussion/list", {})
        store.commit("report/clear")
        if (prev.oid) {
          await store.dispatch("recorder/unsubscribe", {oid: prev.oid})
          delete prev.oid
        }
        if (prev.cid) {
          await store.dispatch("discussion/unsubscribe", {cid: prev.cid})
          delete prev.cid
        }
      }
    }
    else if(mutation.type == "oid"){
      const oid = mutation.payload
      const VIEWER = "viewer"
      const loginType = store.state.auth.loginUser?.type || VIEWER

      await store.dispatch("recorder/unsubscribe", {oid: prev.oid})
      if (loginType != VIEWER) {
        if(oid) await store.dispatch("recorder/subscribe", {oid})
      }

      if(oid && oid != prev.oid){
        store.dispatch("organization/get", {oid})
        store.dispatch("skill/getOrgSettings", {oid})
        store.dispatch("course/list", {oid})
        store.dispatch("member/list", {oid})
        if (loginType != VIEWER) {
          store.dispatch("recorder/list", {oid})
        }
      }
      prev.oid = oid
    }
    else if(mutation.type == "cid"){
      const cid = mutation.payload
      await store.dispatch("discussion/unsubscribe", {cid: prev.cid})
      if(cid) await store.dispatch("discussion/subscribe", {cid})
      if(cid && cid != prev.cid){
        await store.dispatch("discussion/list", {cid})
        delete prev.did
      }
      prev.cid = cid
    }
    else if(mutation.type == "did"){
      const did = mutation.payload
      if(did && did != prev.did){
        await store.dispatch("discussion/get", {id: did, cid: state.cid})
      }
      prev.did = did
    }
  })
  store.dispatch("auth/getUser")
  store.dispatch("timer")
}

const store = new Vuex.Store({
  plugins: [localStore.plugin, sessionStore.plugin, handler],
  modules: {
    auth: {
      namespaced: true,
      state: {
        loginUser: null,
        loginInfo: {},
        loginStatus: null,
        ltik: {},
        ltiClaim: {},
      },
      mutations: {
        loginUser: (state, user) => state.loginUser = user,
        loginInfo: (state, info) => state.loginInfo = info,
        loginStatus: (state, status) => state.loginStatus = status,
        ltik: (state, token) => state.ltik = token,
        ltiClaim: (state, claim) => state.ltiClaim = claim,
      },
      actions: {
        async getUser(context){
          const user = await auth.getLoginUser()
          await context.commit("loginUser", user)
        },
        async signIn(context, params){
          try {
            const user = await auth.signIn(params.username, params.password)
            if (user && user.challengeName === "SOFTWARE_TOKEN_MFA") {
              context.commit("loginStatus", user.challengeName)
              return
            }
            context.commit("loginUser", user)
            store.commit("notification/remove", {msg: "Failed to login"})
            return user
          } catch(e) {
            console.log(e)
            store.commit("notification/error", "Failed to login")
          }
        },
        async signInByToken(context, params){
          await auth.signInByToken(params.username, params.ltik)
          .then((user) => context.commit("loginUser", user))
        },
        async signOut(context){
          await auth.signOut()
          await context.commit("loginUser", null)
        },
      },
    },
    recorder: {
      namespaced: true,
      state: {
        list: [],
        expectedToBeInRec: [],
      },
      mutations: {
        list: (state, list) => state.list = list,
        update: (state, val) => {
          const base = _.find(state.list, {id: val.id})
          if(base) _.assign(base, val)
          else state.list.push(val)
        },
        startRec: (state, list) => state.expectedToBeInRec = _.union(state.expectedToBeInRec, list),
        stopRec: (state, list) => state.expectedToBeInRec = _.difference(state.expectedToBeInRec, list),
      },
      actions: {
        async subscribe(context, params){
          return recorder.Subscribe(params, (r) => {
            context.commit("update", r)
          })
        },
        async unsubscribe(){
          return recorder.Unsubscribe()
        },
        async list(context, params){
          await recorder.list(params)
          .then(async (list) => {
            if(list) await context.commit("list", list)
          })
          .catch(() => store.commit("notification/error", "error.failed-load-recorder-list"))
        },
        async rec(context, params){
          await context.commit("startRec", _.map(params.input,"recorderId"))
          await recorder.rec(params)
          .then(() => {
            setTimeout(() => {
              const failedRecorders = _(store.state.recorder.list)
              .filter((r) => _.includes(_.map(params.input, "recorderId"), r.id))
              .filter((r) => _.includes(store.state.recorder.expectedToBeInRec, r.id))
              .filter((r) => r.status != "Recording")
              .value()

              if (failedRecorders.length != 0) {
                Vue.set(_.find(store.state.course.list, {id: params.cid}), "recordedAt", null)
                store.commit("notification/error", "error.failed-start-recording")
              }
            }, conf.startRecordingTimeoutMS)
          })
          .catch(() => {store.commit("notification/error", "error.failed-start-recording")})
        },
        async stop(context, params){
          await context.commit("stopRec", params.input)
          return recorder.stop(params)
          .catch(() => {store.commit("notification/error", "error.failed-stop-recording")})
        },
        async reboot(context, params){
          return recorder.reboot(params)
          .then(() => store.commit("notification/info", {msg: "info.recorder-restarted", count: params.input.length}))
          .catch(() => {store.commit("notification/error", "error.failed-reboot-recorder")})
        },
        async shutdown(context, params){
          return recorder.shutdown(params)
          .then(() => store.commit("notification/info", {msg: "info.recorder-shutdown", count: params.input.length}))
          .catch(() => {store.commit("notification/error", "error.failed-shutdown-recorder")})
        },
        async assign(context, params) {
          const msg = params.input.assign ? "info.recorder-assigned" : "info.recorder-unassigned"
          return recorder.assign(params)
          .then(() => store.commit("notification/info", {msg: msg , count: params.input.recorders.length}))
          .catch(() => {store.commit("notification/error", "error.failed-assign-recorder")})
        }
      },
    },
    organization: {
      namespaced: true,
      state: {
      },
      mutations: {
        state: (state, val) => {
          Object.keys(val).forEach(key => {
            Vue.set(state, key, val[key])
          })
        },
        clear: (state) => Object.keys(state).forEach((key) => delete state[key]),
      },
      actions: {
        async get(context, params){
          context.commit("clear")
          const org = await orgMng.getOrganization(params)
          if(org){
            if(!org.reportFormat) org.reportFormat = {discussion: [{id: "dreport_v1", name: "標準レポート", lang: "ja"}]}
            if(!org.config) org.config = {usePreset: false, restrictionOnEditor: {simultaneousRecording: false}}
            context.commit("state", org)

            try {
              const isAvailable = await metering.isAvailable(org.ID, Application.DISCUSSION, "analysis")
              org.isRecordingAvailable = isAvailable.value
              context.commit("state", org)
            } catch (e) {
              // 取得失敗時は録音を許可する(fail-open)。最終的な可否はサーバー(discussion の Create)が判定する
              console.error("failed to get metering", e)
              context.commit("state", {isRecordingAvailable: true})
            }
          } else {
            throw new Error("failed to get organization info")
          }
        },
        async updateConfig(context, params){
          await orgMng.updateConfig({oid: params.oid, input: params.input})
          .then(() => context.commit("state", {config: params.input}))
          .then(() => store.commit("notification/info", "info.config-edited"))
        }
      },
    },
    course: {
      namespaced: true,
      state: {
        list: [],
        default: null,
      },
      mutations: {
        list: (state, list) => state.list = list,
        setDefault: (state, oid) => {
          if (!oid) {
            console.warn("oid is not set")
            return
          }
          state.default = oid.replace("org", "crs")
        },
        update: (state, val) => {
          const idx = _.findIndex(state.list, {id: val.id})
          if(idx >= 0) Vue.set(state.list, idx, Object.assign({}, state.list[idx], val))
          else state.list.push(val)
        },
        remove: (state, cid) => {
          _.remove(state.list, {cid})
        }
      },
      actions: {
        async list(context, params){
          await orgMng.listCourses(params)
          .then((list) => context.commit("list", list))
          .catch(() => store.commit("notification/error", "error.failed-get-courses"))
        },
        async edit(context, params){
          await orgMng.updateCourseByDiff({
            oid: params.oid,
            cid: params.id,
            before: _.find(context.state.list, {id: params.id}),
            after: params,
          })
          .then((course) => context.commit("update", course))
          .then(() => store.commit("notification/info", "info.course-edited"))
          .catch((e) => {
            if (e === "course-error") store.commit("notification/error", "error.failed-edit-course-base")
            else if (e === "member-error") store.commit("notification/error", "error.failed-edit-course-member")
            else if (e === "seat-error") store.commit("notification/error", "error.failed-edit-course-seat")
          })
        },
        async editGroup(context, params) {
          await orgMng.setCourseGroup({
            oid: params.oid,
            cid: params.cid,
            gid: params.gid,
            input: params.value,
          })
          await context.dispatch("get", {oid: params.oid, cid: params.cid})
        },
        async get(context, params){
          await orgMng.getCourse({
            oid: params.oid, cid: params.cid
          }).then((crs) =>  context.commit("update", crs))
        },
        async create(context, params){
          await orgMng.createCourse(params)
          .then(() => context.dispatch("list", params))
          .then(() => store.commit("notification/info", "info.course-created"))
          .catch(() => store.commit("notification/error", "error.failed-create-course"))
        },
        async delete(context, params){
          await orgMng.deleteCourse(params)
          .then(() => context.commit("remove", params))
          .then(() => context.dispatch("list", params))
          .then(() => {
            store.commit("notification/info", {
              msg: "info.deleted",
              count: 1,
              params: {item: i18n.tc("Course").toLowerCase()}
            })
          })
          .catch(() => store.commit("notification/error", "error.failed-delete-course"))
        }
      },
    },
    member: {
      namespaced: true,
      state: {
        list: [],
      },
      mutations: {
        list: (state, list) => state.list = list,
        update: (state, val) => {
          const base = _.find(state.list, {id: val.id})
          if(base) _.assign(base, val)
          else state.list.push(val)
        },
        remove: (state, mid) => {
          const index = _.findIndex(state.list, {id: mid})
          state.list.splice(index, 1)
        },
      },
      actions: {
        async list(context, params){
          const list = await orgMng.listMembers(params)
          if(list) await context.commit("list", list)
        },
        async add(context, params){
          try {
            const batchSize = 10
            const miniBatch = _.chunk(params.input, batchSize).map(
              chunk => { return {oid: params.oid, input: chunk} }
            )
            let count = 0
            for(const chunk of miniBatch){
              await orgMng.addMember(chunk)
              count += chunk.input.length
              if(params.progressCb) params.progressCb(count)
            }
            await context.dispatch("list", params)
            store.commit("notification/info", "info.member-added")
          } catch(e) {
            console.log(e)
            store.commit("notification/error", "error.failed-add-member")
          }
        },
        async delete(context, params){
          Promise.all(_.map(params, async (param) => {
            await orgMng.delMember(param)
          }))
          Promise.all(_.map(params, async (param) => {
            await context.commit("remove", param.mid)
          }))
          .then(() => store.commit("notification/info", {
            msg: "info.deleted",
            count: params.length,
            params: {item: i18n.tc("Member", params.length).toLowerCase()}
          }))
        },
        async edit(context, params) {
          await orgMng.updateMember(params)
          .then(() => context.dispatch("list", params))
          .then(() => store.commit("notification/info", "info.member-edited"))
          .catch(() => store.commit("notification/error", "error.failed-edit-member"))
        },
      },
    },
    discussion: {
      namespaced: true,
      state: {
        list: {},
        estimateTime: (d, base) => {
          const N = 5
          const window = (arr) => _.slice(arr, Math.max(0, arr.length - N), arr.length)
          const tDiff = (t1, t2) => t1.diff(t2, "seconds")
          const velocity = (p1, p2) => (p1.progress - p2.progress) / tDiff(p1.updatedAt, p2.updatedAt)

          if (!base.history || base.status != d.status) {
            base.history = []
            base.history.push({
              updatedAt: d.updatedAt,
              progress: d.progress
            })
            base.estimatedTime = null
            return
          }
          if (d.progress == _.last(base.history).progress) return

          base.history.push({
            progress: d.progress,
            updatedAt: d.updatedAt,
            velocity: velocity(d, _.last(base.history))
          })
          const averageVelocity = _.chain(window(base.history))
          .map("velocity")
          .compact()
          .mean()
          .value()
          if (!averageVelocity) return
          const remainingTime = (100 - d.progress) / averageVelocity
          d.estimatedTime = d.updatedAt.clone().add(remainingTime, "seconds").valueOf()
        }
      },
      mutations: {
        list: (state, list) => {
          _.each(list, (v, k) => Vue.set(state.list, k, _.map(v, (d) => {
            const l = state.list[k]
            const base = _.find(l, {id: d.id})
            return base ? Object.assign(base, d) : d
          })))
        },
        update: (state, val) => {
          if (!val) return

          let list = state.list[val.cid]
          if(!list){
            list = []
            Vue.set(state.list, val.cid, list)
          }

          val.updatedAt = store.state.now
          const base = _.find(list, {id: val.id})
          if(base){
            state.estimateTime(val, base)
            _.each(val, (v, k) => {if(base[k] != v) Vue.set(base, k, v)})
          }else{
            list.push(val)
          }
        },
        del: (state, val) => {
          const list = state.list[val.cid]
          const ids = _.map(list, "id")
          const i = _.indexOf(ids, val.id)
          if(i < 0) return
          list.splice(i, 1)
        },
      },
      actions: {
        async subscribe(context, params){
          await discussion.Subscribe(
            params,
            (d) => context.commit("update", d),
            (d) => context.commit("del", {id: d.id, cid: d.cid})
          )
        },
        async unsubscribe(){
          return discussion.Unsubscribe(store.state.cid)
        },
        async list(context, params){
          const list = await discussion.list(params)
          if(list) await context.commit("list", list)
        },
        async get(context, params){
          const d = await discussion.get(params)
          await context.commit("update", d)
          await store.dispatch("audio/getUrl", {did: d.id, cid: d.cid})
        },
        async create(context, params){
          return discussion.Create(params)
        },
        async update(context, params){
          return discussion.Update(params)
        },
        async start(context, params) {
          try {
            const discussions = await store.dispatch("discussion/create", params)

            const recParams = {
              oid: store.state.oid,
              cid: store.state.cid,
              input: _.map(discussions, (d) => {
                const options = _.pick(d.options, ["runAsr", "omitFullChannelAudio"])
                options.isEdge = d.options && d.options.isPrivate || false

                return {
                  recorderId: d.recorder.id,
                  reqId: d.id,
                  dest: d.dest,
                  application: "discussion",
                  options,
                  flexOptions: JSON.stringify({
                    organizationId: store.state.oid,
                    courseId: store.state.cid,
                    discussionId: d.id,
                  }),
                }
              })
            }
            await store.dispatch("recorder/rec", recParams)
          } catch (e) {
            // 失敗(契約なし NO_PLAN 等でサーバーが Create を拒否した場合を含む)は通知し、呼び出し側へ再送出する
            console.error(e)
            store.commit("notification/error", "error.failed-start-recording")
            throw e
          }
        },
        async stop(context, params) {
          const stopParams = {
            oid: store.state.oid,
            input: _.map(params, "r.id"),
          }
          await store.dispatch("recorder/stop", stopParams)
        },
        async del(context, params) {
          try {
            await discussion.Del(params)
            _.each(params, (id) => context.commit("del", { id, cid: context.rootState.cid }))
            store.commit("notification/info", {
              msg: "info.deleted",
              count: params.length,
              params: {item: i18n.tc("Discussion", params.length).toLowerCase()}
            })
          } catch (e) {
            console.error(e)
            store.commit("notification/error", "error.failed-delete-discussion")
            throw e
          }
        },
        async analyze(context, params){
          await discussion.Analyze(params)
          .catch(() => store.commit("notification/error", "error.failed-reanalyzing-discussion"))
        },
        async transfer(context, {discussions, cid}){
          await Promise.all(_.map(discussions, async (d) => {
            await discussion.Update({id: d.id, cid})
            await context.commit("del", {id: d.id, cid: d.cid})
            if(context.state.list[cid]) context.commit("update", {...d, cid})
          }))
          .then(() => {
            store.commit("notification/info", {
              msg: "info.discussion-transfered",
              count: discussions.length,
              timeout: 0,
              action: {
                label: i18n.t("Move to the target course", {course: _.find(store.state.course.list, {id: cid})["name"]}),
                link: {name: "discussion-list", params: {cid : cid}},
              }
            })
          })
          .catch(() => {
            store.commit("notification/error", "error.failed-transfer-discussion")
          })
        },
        async annotate(context, {id, annotations}) {
          await discussion.Update({id, annotations})
          .catch(() => {
            store.commit("notification/error", "error.failed-update-annotations")
          })
        },
      },
    },
    speaker: {
      namespaced: true,
      state: {
        candidates: {},
      },
      mutations: {
        candidate: (state, {did, name, candidates, start, end}) => Vue.set(state.candidates, `${did}/${name}`, {...candidates, start, end}),
      },
      actions: {
        async indexSpeaker(context, params){
          await knowledgebase.indexSpeaker({
            oid: store.state.oid,
            did: params.did,
            mid: params.mid,
            start: params.start,
            end: params.end,
          }).catch((e) => {
            console.error("Failed to index speaker", e)
          })
        },
        async deleteSpeaker(context, params){
          await knowledgebase.deleteSpeaker({
            oid: store.state.oid,
            did: params.did,
            mid: params.mid,
            start: params.start,
            end: params.end,
          }).catch((e) => {
            console.error("Failed to delete speaker embedding", e)
          })
        },
        async fetchCandidatesForDiscussion(context, {did, seats, activity, skipSearch}){
          const results = await Promise.allSettled(seats.map(async (m) => {
            const longest = activity && activity[m.memberId] ? findLongestHighFrame(activity, m.memberId) : null
            if (!longest) {
              console.warn(`No high activity frames found for ${m.memberId}`)
              await store.dispatch("speaker/getCandidates", {
                did, name: String(m.position), noClip: true
              })
            } else {
              const midSec = Math.floor((longest.start + longest.end) / 2) * 5
              await store.dispatch("speaker/getCandidates", {
                did, name: String(m.position),
                start: Math.max(0, midSec - 5), end: midSec + 5,
                skipSearch,
              })
            }
          }))
          // 座席ごとに個別通知すると座席数だけ積み上がるため、失敗があれば1回だけ通知する
          if (results.some(r => r.status === "rejected"))
            store.commit("notification/error", "error.failed-get-speaker-candidates")
        },
        async getCandidates(context, params){
          const existing = store.state.speaker.candidates[`${params.did}/${params.name}`]
          if (existing != null) return

          if (params.noClip)
            return context.commit("candidate", {did: params.did, name: params.name, candidates: {result: []}})

          if (params.start == null || params.end == null) {
            return
          }

          // たまごディスカッションなど話者候補の提示が対象外のケースでは、音声の聞き返し(start/end)は保持しつつKB検索自体は行わない
          if (params.skipSearch)
            return context.commit("candidate", {did: params.did, name: params.name, candidates: {result: []}, start: params.start, end: params.end})

          context.commit("candidate", {did: params.did, name: params.name, candidates: {}, start: params.start, end: params.end})

          const doSearch = () => knowledgebase.searchSpeaker({
            oid: store.state.oid,
            did: params.did,
            start: params.start,
            end: params.end,
          })
          await doSearch()
          .then(res => context.commit("candidate", {did: params.did, name: params.name, candidates: res, start: params.start, end: params.end}))
          .catch((e) => {
            // 失敗時はcommitせず、次回getCandidatesが呼ばれた際に再検索できるようにする
            Vue.delete(store.state.speaker.candidates, `${params.did}/${params.name}`)
            throw e
          })
        },
      },
    },
    audio: {
      namespaced: true,
      state: {
        urls: {},
      },
      mutations: {
        url: (state, {did, url}) => Vue.set(state.urls, did, url),
      },
      actions: {
        async getUrl(context, params){
          if (!store.state.organization.apikey) {
            const oid = store.state.oid
            await store.dispatch("organization/get", {oid})
          }

          await discussion.GetAudioUrl({
            id: params.did,
            cid: params.cid,
            apikey: store.state.organization.apikey
          })
          .then(url => context.commit("url", {did: params.did, url}))
          .catch(() => store.commit("notification/error", "error.failed-get-audio"))
        },
      },
    },
    report: {
      namespaced: true,
      state: {
        discussion: {},
      },
      mutations: {
        discussion: (state, val) => {
          const report = _.get(state.discussion, val.id)
          const report_format = utils.reportFormatString({id: val.format, lang: val.lang})
          if(report) Vue.set(report, report_format, val)
          else Vue.set(state.discussion, val.id, {[report_format]: val})
        },
        clearDiscussion: (state, id) => Vue.set(state.discussion, id, {}),
        clear: (state) => state.discussion = {}
      },
      actions: {
        async discussion(context, params){
          if(!params.format) params.format = "dreport_v1"
          if(!params.lang) params.lang = "ja"
          const d = await report.discussion(params)
          await context.commit("discussion", {id: params.id, url: d, format: params.format, lang: params.lang})
        },
      },
    },
    skill: {
      namespaced: true,
      state: {
        estimations: {},
        enabled: false,
        adminOnly: true,
        rubrics: [],
        defaultRubricId: DEFAULT_RUBRIC_ID,
        courseDefaultRubricIds: {},
      },
      getters: {
        rubrics: (state) => state.rubrics || [],
        rubricItems: (state, getters) => getters.rubrics.map((rubric) => ({
          text: rubric.name,
          value: rubric.id,
        })),
        hasCustomRubric: (state, getters) => (
          getters.rubrics.some((rubric) => rubric.id !== DEFAULT_RUBRIC_ID)
        ),
      },
      mutations: {
        setEstimation: (state, {did, value}) => {
          Vue.set(state.estimations, did, value)
        },
        clearEstimation: (state, did) => {
          Vue.delete(state.estimations, did)
        },
        setEnabled: (state, val) => {
          state.enabled = val
        },
        setAdminOnly: (state, val) => {
          state.adminOnly = val
        },
        setRubrics: (state, rubrics) => {
          state.rubrics = rubrics
        },
        setDefaultRubricId: (state, rubricId) => {
          state.defaultRubricId = rubricId
        },
        setCourseDefaultRubricIds: (state, rubricIds) => {
          state.courseDefaultRubricIds = rubricIds
        },
        setCourseDefaultRubricId: (state, {cid, rubricId}) => {
          Vue.set(state.courseDefaultRubricIds, cid, rubricId)
        },
        clear: (state) => {
          state.estimations = {}
          state.enabled = false
          state.adminOnly = true
          state.rubrics = []
          state.defaultRubricId = DEFAULT_RUBRIC_ID
          state.courseDefaultRubricIds = {}
        },
      },
      actions: {
        async getOrgSettings(context, {oid}){
          try {
            const res = await ailib.getOrgSettings({oid})
            context.commit("setEnabled", res.enable_skill_estimation ?? false)
            context.commit("setAdminOnly", res.is_skill_estimation_admin_only ?? true)
            context.commit("setRubrics", res.rubrics)
            context.commit("setDefaultRubricId", res.default_rubric_id ?? DEFAULT_RUBRIC_ID)
            context.commit("setCourseDefaultRubricIds", res.course_default_rubric_ids || {})
          } catch(e) {
            console.error(e)
            context.commit("setEnabled", false)
          }
        },
        async updateOrgSettings(context, {oid, input}){
          const res = await ailib.updateOrgSettings({oid}, input)
          if("is_skill_estimation_admin_only" in input) {
            context.commit("setAdminOnly", !!input.is_skill_estimation_admin_only)
          }
          if("default_rubric_id" in input) {
            context.commit("setDefaultRubricId", input.default_rubric_id)
          }
          return res
        },
        async updateCourseSettings(context, {oid, cid, input}){
          const res = await ailib.updateCourseSettings({oid, cid}, input)
          if("default_rubric_id" in input) {
            context.commit("setCourseDefaultRubricId", {cid, rubricId: input.default_rubric_id})
          }
          return res
        },
        async getEstimation(context, {oid, did, rubricId = DEFAULT_RUBRIC_ID, force = false}){
          const cached = context.state.estimations[did]
          if(!force && cached && cached.rubricId === rubricId) {
            if(cached.state === "done" && cached.estimation) return cached
            if(cached.state === "idle") return cached
          }

          try {
            const res = await ailib.getSkillEstimation({oid, did, rubricId})
            const value = {
              state: res.state,
              estimation: res ?? null,
              rubricId,
            }
            context.commit("setEstimation", {did, value})
            return value
          } catch (error) {
            if(!isNotFoundError(error)) throw error

            const value = {
              state: "idle",
              estimation: null,
              rubricId,
            }
            context.commit("setEstimation", {did, value})
            return value
          }
        },
        async createEstimation(context, {oid, did, rubricId = DEFAULT_RUBRIC_ID}){
          const res = await ailib.createSkillEstimation({oid, did, rubricId})
          context.commit("setEstimation", {
            did,
            value: {state: "doing", estimation: null, rubricId},
          })
          return res
        },
      },
    },
    notification: {
      namespaced: true,
      state: {
        list: [],
      },
      mutations: {
        error: (state, e) => {
          const base = {id: new Date().valueOf(), show: true, timeout: 0, error: true}
          if(_.isString(e)) e = {msg: e}
          state.list.push(Object.assign(base, e))
        },
        info: (state, i) => {
          const base = {id: new Date().valueOf(), show: true, timeout: 3000}
          if(_.isString(i)) i = {msg: i}
          state.list.push(Object.assign(base, i))
        },
        remove: (state, predicate) => {
          state.list = _.reject(state.list, predicate)
        }
      },
    },
    ui: {
      namespaced: true,
      modules: {
        filter: {
          namespaced: true,
          state: {},
          mutations: {
            add: (state, {key, value}) => {
              state[key] = value
            },
            remove: (state, key) => {
              delete state[key]
            },
          },
        },
        sort: {
          namespaced: true,
          state: {},
          mutations: {
            add: (state, {key, value}) => {
              state[key] = value
            },
          },
        },
        rowsPerPage: {
          namespaced: true,
          state: {
            value: null
          },
          mutations: {
            set: (state, value) => {
              state.value = value
            },
          },
        },
        recording: {
          namespaced: true,
          state: {
            topics: [],
            listview: null,
          },
          mutations: {
            topic: (state, {value, cid}) => {
              const topic = _.find(state.topics, {cid: cid})
              if(topic) topic.value = value
              else state.topics.push({value, cid})
            },
            listview: (state, value) => {
              state.listview = value
            }
          },
        }
      }
    },
    favorites: {
      namespaced: true,
      state: {
        list: {}
      },
      mutations: {
        addFavorite(state, itemId) {
          Vue.set(state.list, itemId, true)
        },
        removeFavorite(state, itemId) {
          Vue.delete(state.list, itemId)
        }
      },
      actions: {
        toggleFavorite({ commit, state }, itemId) {
          const current = !!state.list[itemId]
          if (current) {
            commit("removeFavorite", itemId)
          } else {
            commit("addFavorite", itemId)
          }
        }
      },
      getters: {
        isFavorite: (state) => (itemId) => !!state.list[itemId]
      },
    },
  },
  state: {
    oid: null,
    cid: null,
    did: null,
    now: moment(),
    type: null,
  },
  mutations: {
    oid: (state, id) => state.oid = id,
    cid: (state, id) => state.cid = id,
    did: (state, id) => state.did = id,
    now: (state, now) => state.now = now,
    type: (state, val) => state.type = val,
  },
  actions: {
    oid: async ({commit}, params) => await commit("oid", params),
    cid: async ({commit}, params) => await commit("cid", params),
    did: async ({commit}, params) => await commit("did", params),
    timer: async ({commit}) => {
      for(;;){
        await commit("now", moment())
        await new Promise((resolve) => setTimeout(resolve, 1000))
      }
    },
  },
})
export default store

Vue.mixin({
  computed: {
    accountType(){
      return store.state.type
    },
    isAdmin(){
      return this.accountType === "admin"
    },
    hasEditorPermission(){
      return _.includes(["admin", "editor"], this.accountType)
    },
    now(){
      return store.state.now
    },
    oid(){
      return store.state.oid
    },
    loginUser(){
      const loginUser = _.pick(this.$store.state.auth.loginInfo, ["oid", "username"])
      loginUser.id = loginUser.username?.substring(loginUser.oid.length+1)
      return loginUser
    },
    isLoggedIn(){
      return !!store.state.auth.loginUser
    },
  },
  methods: {
    isLoginUser(mid){
      return mid == this.loginUser.id
    },
    getMember(mid){
      return this.$store.state.member.list.find((m) => m.id === mid)
    },
    getCourseMembers(course){
      if(!course) return []
      const members = _.map(course.members, (m) => this.getMember(m.id))
      return _.sortBy(members.filter((m) => m), "name")
    }
  }
})
